import { useState, useEffect } from 'react';
import { clients as clientsApi, delivery as deliveryApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import FnuciBadge from '../common/FnuciBadge.jsx';

const SCAN_LABELS = {
  ocr_auto: 'OCR auto',
  ocr_rotated: 'OCR rotation',
  manual: 'Manuel',
};

const STATUS_COLORS = {
  in_stock: 'bg-gray-200 text-gray-700',
  assigned: 'bg-blue-200 text-blue-800',
  delivered: 'bg-green-200 text-green-800',
  problem: 'bg-red-200 text-red-800',
};

const STATUS_LABELS = {
  in_stock: 'En stock',
  assigned: 'Attribué',
  delivered: 'Livré',
  problem: 'Problème',
};

/**
 * List of items (units) assigned to a client.
 * @param {{ clientId: string }} props
 */
export default function ClientItems({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({}); // { [itemId]: true }
  const { campaigns, activeCampaign, viewAsRole } = useAuth();
  const campaignEntry = campaigns.find((c) => c.campaignId === activeCampaign);
  const effectiveRole = viewAsRole || campaignEntry?.role;
  const canRetry = effectiveRole === 'admin' || effectiveRole === 'coordinator';

  useEffect(() => {
    clientsApi.getItems(clientId)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  // Polling auto si au moins un item est en pending - ramene le statut a jour
  // sans necessiter un refresh manuel.
  useEffect(() => {
    const hasPending = items.some((it) => it.fnuci_status === 'pending');
    if (!hasPending) return;
    const t = setInterval(() => {
      clientsApi.getItems(clientId).then(setItems).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [items, clientId]);

  const handleRetry = async (itemId) => {
    setRetrying((prev) => ({ ...prev, [itemId]: true }));
    try {
      await deliveryApi.retryFnuci(itemId);
      const fresh = await clientsApi.getItems(clientId);
      setItems(fresh);
    } catch {
      // erreur loguee cote serveur ; on ne bloque pas l'UI
    } finally {
      setRetrying((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  if (loading) return <p className="text-gray-400 text-sm py-2">Chargement des unités...</p>;
  if (items.length === 0) return <p className="text-gray-400 text-sm py-2">Aucune unite attribuee.</p>;

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <h3 className="font-semibold text-sm mb-3">Unités ({items.length})</h3>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
            <div>
              <span className="font-mono text-xs">{item.serial_number}</span>
              {item.secondary_id && (
                <span className="text-gray-400 text-xs ml-2">({item.secondary_id})</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {item.scan_method && (
                <span className="text-xs text-gray-400">{SCAN_LABELS[item.scan_method] || item.scan_method}</span>
              )}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>
                {STATUS_LABELS[item.status] || item.status}
              </span>
              <FnuciBadge
                status={item.fnuci_status}
                lastError={item.fnuci_last_error}
                onRetry={canRetry ? () => handleRetry(item.id) : undefined}
                retrying={!!retrying[item.id]}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
